import { Box, BoxProps, Flex, Skeleton } from "@chakra-ui/react";
import { ReactNode, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaAlert, TenantContext } from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { HeaderBox } from "@/components/header-box";
import { RanaLink } from "@/components/rana-link";
import { getProjectTabQueryParam } from "@/features/project";
import { ProjectFilePreview } from "@/features/project-file-preview";
import { FileDataTypeEnum, RanaFileIcon } from "@/lib/rana-file-data-type";
import { useFileQuery } from "@/queries/query-file";
import { ProjectContext } from "@/stores/project-provider";
import { ProjectFile } from "@/types/api-model-simple-names";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { RESULT_SPACING } from "../consts/result-spacing";

interface ResultFileItemProps {
  fileId: string;
  fileRef: string | undefined;
  reduced: boolean;
}

const DEFAULT_WRAPPER_PROPS: BoxProps = { mb: RESULT_SPACING, _last: { mb: 0 } };

const getWrapperProps = (reduced: boolean) => {
  return reduced ? { mb: 0 } : DEFAULT_WRAPPER_PROPS;
};

export function ResultFileItem({ fileId, fileRef, reduced }: ResultFileItemProps) {
  const { tenant } = useContext(TenantContext);
  const { slug } = useContext(ProjectContext);
  const intl = useIntl();

  const fileQuery = useFileQuery(fileId, fileRef);
  const props = getWrapperProps(reduced);

  if (fileQuery.isPending) {
    return <ResultFileLoading reduced={reduced} />;
  }

  const file = fileQuery.data;
  if (!file) {
    return (
      <ResultFileError reduced={reduced}>
        <FormattedMessage
          id="processresults.file_not_found"
          defaultMessage="File `{fileId}` with ref {fileRef} not found"
          values={{ fileId, fileRef }}
        />
      </ResultFileError>
    );
  }

  const isFile = file.type === "file";
  const dataType = file.dataType;

  return (
    <Box key={fileId} {...props}>
      <ProjectFilePreview
        name={
          <Flex alignItems="center">
            <RanaFileIcon fileOrDirectory={file as ProjectFile} boxSize={6} mx={1} />{" "}
            <RanaLink
              to={`/${tenant}/projects/${slug}`}
              queryParams={`${getProjectTabQueryParam("files")}&${convertFileIdToQueryParams(
                file.id,
                fileRef
              )}`}
              title={intl.formatMessage({
                id: "processresults.open_file_preview",
                defaultMessage: "Open in file preview",
              })}
            >
              {getNameFromFileId(file.id)}
            </RanaLink>
            &nbsp;
            {isFile && dataType !== FileDataTypeEnum.Schematisation
              ? `(${dataType ? `${dataType}, ` : ""}${displayBytes(file.size)})`
              : "(Rana Model schematisation)"}
          </Flex>
        }
        fileRef={fileRef}
        file={file as ProjectFile}
      />
    </Box>
  );
}

export const ResultFileLoading = ({ reduced }: { reduced: boolean }) => {
  const props = getWrapperProps(reduced);
  return (
    <Skeleton {...props}>
      <HeaderBox name="Loading" />
    </Skeleton>
  );
};

export const ResultFileError = ({
  reduced,
  children,
}: {
  reduced: boolean;
  children: ReactNode;
}) => {
  const props = getWrapperProps(reduced);
  return (
    <Box {...props}>
      <HeaderBox name="Error">
        <RanaAlert mx={8} status="error">
          {children}
        </RanaAlert>
      </HeaderBox>
    </Box>
  );
};
